import { FC, useEffect, useCallback } from 'react';

import type {
  GameCommunicationControllerConnectorProps,
  IGameCommunicationProcessorFactory,
} from '@pp/core/src/services/gameCommunication/types';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';

import { GameCommunicationMultiplierProcessor } from './controllers/gameCommunication/processors/GameCommunicationMultiplierProcessor';
import { broadcasterCommunicationService } from './services/broadcasterCommunicationService';
import { useGameConfigSlice } from './store/slices/config/selectors';

const gameCommunicationProcessors: IGameCommunicationProcessorFactory[] = [GameCommunicationMultiplierProcessor];
const gameCommunicationSenders: any[] = [];

export const BroadcasterCommunicationControllerConnector: FC<GameCommunicationControllerConnectorProps> = ({
  gameStore,
  config,
}): null => {
  const JSESSIONID = useConfigSlice.JSESSIONID();
  const tableId = useConfigSlice.tableId();
  const tableConfig = useConfigSlice.tableConfig();
  const isGamePaused = useGameStateSlice.getIsGamePaused();
  const gameChannel = useGameStateSlice.gameChannel();
  const isGameAbandoned = useGameStateSlice.getIsGameAbandoned();
  const isUserInfoReady = useGameStateSlice.userInfoReady();
  const broadcasterWsAddress = useGameConfigSlice.getBroadcasterWsAddress();
  const launchSource = useConfigSlice.launchSource();

  const shouldHaltCommunication = useCallback((): boolean => {
    return Boolean(tableConfig?.tableClosed);
  }, [tableConfig]);

  // Setup processors, senders, connection observer
  useEffect(() => {
    const processors = gameCommunicationProcessors.concat(config?.processors ?? []);
    processors.forEach((processor) => broadcasterCommunicationService.setProcessor(processor, gameStore));

    const senders = gameCommunicationSenders.concat(config?.senders ?? []);
    if (gameStore) broadcasterCommunicationService.setStore(gameStore);
    senders.forEach((sender) => broadcasterCommunicationService.setSender(sender));

    if (config?.connectionObserver) {
      broadcasterCommunicationService.setConnectionObserver(config.connectionObserver);
    }
  }, [config, gameStore]);

  // Pause/resume WebSocket connection
  useEffect(() => {
    broadcasterCommunicationService.stopStart(isGamePaused);
  }, [isGamePaused]);

  // Kill communication if game is abandoned
  useEffect(() => {
    if (isGameAbandoned) {
      broadcasterCommunicationService.killCommunication();
    }
  }, [isGameAbandoned]);

  // Mark communication as initialized
  useEffect(() => {
    if (gameChannel) {
      broadcasterCommunicationService.setInitialized();
      broadcasterCommunicationService.communicationStrategy.setGameChannel(gameChannel);
    }
  }, [gameChannel]);

  // Connect when all required data is ready
  useEffect(() => {
    if (!shouldHaltCommunication() && broadcasterWsAddress && JSESSIONID && tableId && isUserInfoReady) {
      broadcasterCommunicationService.setCredentials({
        url: broadcasterWsAddress,
        jsessionId: JSESSIONID,
        tableId,
        launchSource: launchSource ?? undefined,
      });
      broadcasterCommunicationService.init();
    }
  }, [shouldHaltCommunication, tableConfig, tableId, JSESSIONID, isUserInfoReady, broadcasterWsAddress, launchSource]);

  return null;
};
